// HEIC decoder — lazy-load libheif-js only when a HEIC file is detected.
// libheif-js bundle is ~700 KB; we don't pay this cost unless used.

export function isHeicFile(file: File): boolean {
  const name = file.name.toLowerCase();
  if (name.endsWith(".heic") || name.endsWith(".heif")) return true;
  return file.type === "image/heic" || file.type === "image/heif";
}

/**
 * Decode an HEIC file to a PNG Blob.
 * Loads libheif-js dynamically on first call so non-HEIC users don't pay.
 */
export async function decodeHeic(file: File): Promise<Blob> {
  const libheif = await loadLibheif();
  const buf = await file.arrayBuffer();
  const decoder = new libheif.HeifDecoder();
  const data = decoder.decode(buf);
  if (!data || data.length === 0) {
    throw new Error(`HEIC decode failed: ${file.name}`);
  }
  const image = data[0]!;
  const w = image.get_width();
  const h = image.get_height();

  const canvas = new OffscreenCanvas(w, h);
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("could not acquire 2d context for HEIC decode");
  }
  const imageData = ctx.createImageData(w, h);

  await new Promise<void>((resolve, reject) => {
    image.display(imageData, (display: ImageData | null) => {
      if (!display) {
        reject(new Error(`HEIC display callback returned null: ${file.name}`));
        return;
      }
      resolve();
    });
  });

  ctx.putImageData(imageData, 0, 0);
  return canvas.convertToBlob({ type: "image/png" });
}

interface LibheifModule {
  HeifDecoder: new () => HeifDecoder;
}
interface HeifDecoder {
  decode(buf: ArrayBuffer): HeifImage[];
}
interface HeifImage {
  get_width(): number;
  get_height(): number;
  display(target: ImageData, cb: (data: ImageData | null) => void): void;
}

let _libheifPromise: Promise<LibheifModule> | null = null;

function loadLibheif(): Promise<LibheifModule> {
  if (!_libheifPromise) {
    _libheifPromise = (async () => {
      // dynamic import → Vite emits a separate chunk; only loaded on demand
      const mod = (await import("libheif-js")) as unknown as { default?: LibheifModule } & LibheifModule;
      return (mod.default ?? mod) as LibheifModule;
    })();
  }
  return _libheifPromise;
}
